import { Marker, Popup } from "react-leaflet";
import { useState, useContext } from "react";
import { IncidentIcon, PendingIncidentIcon } from "./Icons";
import api from "../../services/api-service";
import ImageLightbox from "../ImageLightbox";
import { AuthContext } from "../../contexts/AuthContext";
import "../../styles/popup.css";
import { useAlert } from "../../contexts/AlertContext";
import { formatDateTimeCET } from "../../util/DateFormatter";

export default function IncidentMarkers({
  incidents,
  pendingIncidents,
  onStatusChange,
}) {
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [descriptionLanguage, setDescriptionLanguage] = useState({});

  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);

  const updateStatus = async (incidentId, status) => {
    setLoading(true);
    try {
      await api.put("/incidents", {
        incidentId,
        incidentStatus: status,
      });

      if (onStatusChange) {
        onStatusChange(incidentId, status);
      }

      showAlert("Incident successfully updated", "success");
    } catch (err) {
      console.error("Failed to update incident status", err);
      showAlert("Failed to update incident", "error");
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  const toggleLanguage = (incidentId) => {
    setDescriptionLanguage((prev) => ({
      ...prev,
      [incidentId]: prev[incidentId] === "en" ? "sr" : "en",
    }));
  };

  const getDisplayedDescription = (incident) => {
    const selectedLanguage = descriptionLanguage[incident.id] || "sr";

    if (selectedLanguage === "en") {
      return (
        incident.descriptionEn || incident.descriptionSr || "No description"
      );
    }

    return incident.descriptionSr || incident.descriptionEn || "No description";
  };

  const getLanguageLabel = (incident) => {
    const selectedLanguage = descriptionLanguage[incident.id] || "sr";
    return selectedLanguage.toUpperCase();
  };

  const renderImages = (images) => (
    <div className="image-preview">
      {images.map((url, index) => (
        <div key={index} className="image-item">
          <img
            src={url}
            alt={`incident-${index}`}
            onClick={() => openLightbox(images, index)}
          />
        </div>
      ))}
    </div>
  );

  const renderDescriptionBlock = (incident) => (
    <div className="popup-description-section">
      <div className="popup-description-header">
        <span className="popup-section-title">
          Description ({getLanguageLabel(incident)})
        </span>

        {incident.descriptionSr && incident.descriptionEn && (
          <button
            type="button"
            className="translate-btn"
            onClick={() => toggleLanguage(incident.id)}
          >
            {getLanguageLabel(incident) === "SR" ? "EN" : "SR"}
          </button>
        )}
      </div>

      <p className="popup-description-text">
        {getDisplayedDescription(incident)}
      </p>
    </div>
  );

  const renderPopupContent = (incident, showActions = false) => (
    <div className="popup-card">
      <div className="popup-header">
        <span className="popup-date">
          {formatDateTimeCET(incident.createdAt)}
        </span>
        <span className="popup-type-badge">{incident.typeName}</span>
        {incident.subtypeName && (
          <span className="popup-type-badge">{incident.subtypeName}</span>
        )}
      </div>

      <div className="popup-body">
        {renderDescriptionBlock(incident)}

        {incident.imageUrls?.length > 0 && (
          <div className="popup-images-section">
            <span className="popup-section-title">Images</span>
            <div className="popup-images">
              {renderImages(incident.imageUrls)}
            </div>
          </div>
        )}
      </div>

      {showActions && user?.role === "MODERATOR" && (
        <div className="popup-actions">
          <button
            className="approve-btn"
            onClick={() => updateStatus(incident.id, "APPROVED")}
            disabled={loading}
          >
            Approve
          </button>
          <button
            className="reject-btn"
            onClick={() => updateStatus(incident.id, "REJECTED")}
            disabled={loading}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {incidents.map((incident) => (
        <Marker
          key={incident.id}
          position={[incident.location.latitude, incident.location.longitude]}
          icon={IncidentIcon}
        >
          <Popup className="custom-popup">
            {renderPopupContent(incident, false)}
          </Popup>
        </Marker>
      ))}

      {pendingIncidents.map((incident) => (
        <Marker
          key={incident.id}
          position={[incident.location.latitude, incident.location.longitude]}
          icon={PendingIncidentIcon}
        >
          <Popup className="custom-popup">
            {renderPopupContent(incident, true)}
          </Popup>
        </Marker>
      ))}

      {lightboxOpen && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={photoIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
